import { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { parentApi, timetablesApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar, Clock, DownloadSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';

const DAY_KEYS = ['dayMonday', 'dayTuesday', 'dayWednesday', 'dayThursday', 'dayFriday', 'daySaturday'];

const ParentTimetable = () => {
  const { t, language } = useLanguage();
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState('');
  const [data, setData] = useState({ class_name: '', school_year: '', slots: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    parentApi.getChildren()
      .then(({ data }) => {
        setChildren(data || []);
        if ((data || []).length) setSelectedChild(data[0].id);
        else setLoading(false);
      })
      .catch(() => { toast.error(t('error')); setLoading(false); });
  }, []);

  useEffect(() => {
    if (!selectedChild) return;
    setLoading(true);
    timetablesApi.getParentTimetable(selectedChild)
      .then(({ data }) => setData(data))
      .catch(() => toast.error(t('error')))
      .finally(() => setLoading(false));
  }, [selectedChild]);

  const slotsByDay = Array.from({ length: 6 }, () => []);
  (data.slots || []).forEach((s) => {
    if (s.day_of_week >= 0 && s.day_of_week < 6) slotsByDay[s.day_of_week].push(s);
  });
  slotsByDay.forEach((arr) => arr.sort((a, b) => a.start_time.localeCompare(b.start_time)));

  return (
    <div className="space-y-6" data-testid="parent-timetable-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <Calendar size={28} className="text-[#10B981]" weight="duotone" />
          <h1 className="font-outfit text-3xl font-bold text-[#0F172A]">{t('timetable')}</h1>
          {data.class_name && <span className="px-2 py-1 rounded-full text-xs bg-[#10B981]/10 text-[#10B981]">{data.class_name}</span>}
        </div>
        {children.length > 1 && (
          <div className="flex items-center gap-2">
            <Label>{t('myChildren')}:</Label>
            <Select value={selectedChild} onValueChange={setSelectedChild}>
              <SelectTrigger className="w-60" data-testid="parent-tt-child"><SelectValue /></SelectTrigger>
              <SelectContent>
                {children.map(c => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <Button variant="outline" onClick={async () => {
          try {
            const { data } = await timetablesApi.exportParentPdf(selectedChild);
            const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
            const a = document.createElement('a'); a.href = url;
            a.download = 'emploi_du_temps.pdf';
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
          } catch { toast.error(t('error')); }
        }} disabled={!selectedChild || (data.slots || []).length === 0} data-testid="export-parent-tt-pdf">
          <DownloadSimple size={16} className="mr-1" />PDF
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#10B981]" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {DAY_KEYS.map((key, idx) => (
            <Card key={key} className="border border-[#E2E8F0] shadow-none" data-testid={`p-day-${idx}`}>
              <CardHeader className="pb-2"><CardTitle className="font-outfit text-base">{t(key)}</CardTitle></CardHeader>
              <CardContent className="space-y-2 pt-0">
                {slotsByDay[idx].length === 0 ? (
                  <p className="text-sm text-[#64748B] py-4 text-center">{t('noData')}</p>
                ) : (
                  slotsByDay[idx].map((s) => (
                    <div key={s.id} className="p-3 rounded-lg border border-[#E2E8F0] bg-[#F8FAFC]">
                      <div className="flex items-center gap-2 mb-1">
                        <Clock size={14} className="text-[#10B981]" />
                        <span className="font-mono text-xs text-[#10B981]">{s.start_time} - {s.end_time}</span>
                      </div>
                      <p className="font-outfit font-semibold text-sm">{s.subject}</p>
                      {s.teacher_name && <p className="text-xs text-[#64748B]">{s.teacher_name}</p>}
                      {s.room && <p className="text-xs text-[#64748B]">{language === 'fr' ? 'Salle' : 'Room'}: {s.room}</p>}
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ParentTimetable;
